// @CUSTOM: The view and accompanying definition extensions are from adventureworks@9.47. Any custom changes noted are
// edits to the adventureworks version, not core.

/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AddToCartBehavior } from '@msdyn365-commerce/components';
import MsDyn365, { getUrlSync } from '@msdyn365-commerce/core';
import { ICartIconViewProps, ICartViewProps, IFlyoutCartLineItemViewProps } from '@msdyn365-commerce-modules/cart';
import { Button, getPayloadObject, INodeProps, KeyCodes, Modal, Node, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React from 'react';

import { ICartIconProps as ICartIconExtensionProps } from '../definition-extensions/cart-icon.ext.props.autogenerated';

//----------------------------------------------------------
// @CUSTOM: Kit custom imports
//----------------------------------------------------------
import { ICartIconData } from '@msdyn365-commerce-modules/cart/src/modules/cart-icon/cart-icon.data';
import { attrNames } from '../../../utilities/global-constants';
import { convertCartAttributes } from '../../../utilities/data-attribute-parser';
import { CartIconComponent } from './components/carticon.component';
import { CartLineItemComponent} from './components/cartlineitem.component';
//----------------------------------------------------------

//----------------------------------------------------------
// @CUSTOM: Kit display - get elective cart lines tied to
// kit item
//----------------------------------------------------------
const getElectiveCartLines = (kitCartLine: IFlyoutCartLineItemViewProps, cartLines: IFlyoutCartLineItemViewProps[]) => {
    return cartLines.filter(cartLine => {
        const kitAttributes = kitCartLine.data?.cartline.AttributeValues;
        const parsedKitAttributes = kitAttributes && convertCartAttributes(kitAttributes);
        const attributes = cartLine.data?.cartline.AttributeValues;
        const parsedAttributes = attributes && convertCartAttributes(attributes);
        return parsedKitAttributes && parsedAttributes && parsedKitAttributes[attrNames.kitLineChildAssociation] === parsedAttributes[attrNames.kitLineParentAssociation];
    });
};
//----------------------------------------------------------

//----------------------------------------------------------
// @CUSTOM: Kit display - remove kit item alongside tied
// elective items
//----------------------------------------------------------
const removeKitItem = async (kitCartLine: IFlyoutCartLineItemViewProps, cartLines: IFlyoutCartLineItemViewProps[], cartData: ICartIconData) => {
    const electiveCartLines = getElectiveCartLines(kitCartLine, cartLines);

    if (cartData.cart.result) {
        const input = {
            cartLineIds: [kitCartLine.data.cartline.LineId!.toString()]
        };

        if (electiveCartLines) {
            electiveCartLines.forEach(electiveCartLine => {
                input.cartLineIds.push(electiveCartLine.data.cartline.LineId!.toString());
            });
        }

        cartData.cart.result.removeCartLines(input);
    }
};
//----------------------------------------------------------

/**
 * ICartIconViewState: Interface for Cart Icon View State.
 */
interface ICartIconViewState {
    isModalOpen: boolean;
}

/**
 * Render Cart lines.
 * @param cartLines - Flyout cart line view props.
 * @param props - Cart icon view props.-
 * @returns JSX Element.
 */
const renderCartlines = (cartLines: IFlyoutCartLineItemViewProps[] | undefined, props: ICartIconViewProps): (JSX.Element | null)[] | null => {      // @CUSTOM: Kit display - updated return type to account for no cart lines being returned
    if (!cartLines) {
        props.context.telemetry.error('Cartlines content is empty, module wont render');
        return null;
    }

    return cartLines.map((cartLine, index) => {
        //--------------------------------------------------
        // @CUSTOM: Kit display
        //--------------------------------------------------

        // Minicart does not use cartlineitem.component extension by default on other themes extended from base, so
        // have to force import it and replace the original component
        const cartLineProps = React.isValidElement(cartLine.cartline) && cartLine.cartline.props;
        const cartLineOverride = <CartLineItemComponent {...cartLineProps} />;

        const cartLineData = cartLine.data?.cartline;
        const lineAttributes = cartLineData?.AttributeValues && convertCartAttributes(cartLineData.AttributeValues);
        const kitElectives = lineAttributes && lineAttributes[attrNames.kitElectives];

        // Do not return kit elective cart lines
        if (lineAttributes && lineAttributes[attrNames.kitLineParentAssociation]) {
            return null;
        }

        const removeProps = React.isValidElement(cartLine.remove) && cartLine.remove.props;
        //--------------------------------------------------

        const cartLineIndex = index;
        return (
            <Node {...props.miniCartItemWrapper} key={cartLineIndex}>
                {/* @CUSTOM: Replace original cartlineitem component {cartLine.cartline} with extended one to show cartline customizations */}
                <Node className={classnames(cartLine.storeLocation ? 'msc-cart-line__set-net-price' : '')}>{cartLineOverride}</Node>
                {cartLine.storeLocation}
                {/* @CUSTOM: Replaced Remove button below with custom remove cartline handler if item is a kit with electives */}
                {kitElectives
                    ? <Button {...removeProps} onClick={() => removeKitItem(cartLine, cartLines, props.data)} />
                    : cartLine.remove
                }
            </Node>
        );
    });
};

/**
 * Initiate total price.
 * @param props - Cart icon view props.
 * @returns JSX Element.
 */
const renderTotalPrice = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
    const cart = props.data.cart.result ?? undefined;
    const price = cart && !cart.hasInvoiceLine && (cart.cart.TotalAmount || undefined);
    const totalPrice = price
        ? props.context.cultureFormatter.formatCurrency(price)
        : props.context.cultureFormatter.formatCurrency(props.resources.emptyPrice);
    return cart && !cart.hasInvoiceLine ? (
        <div className='ms-cart-icon__subTotalText'>
            <span className='ms-cart-icon__subTotalPriceText'>
                {props.resources.totalPriceFormatString}
                <span className='ms-cart-icon__subTotalPrice'>{totalPrice}</span>
            </span>
        </div>
    ) : (
        <></>
    );
};

/**
 * Initiate subtotal message.
 * @param props - Cart icon view props.
 * @returns JSX Element.
 */
const renderSubtotalMessage = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
    const cart = props.data.cart.result ?? undefined;
    return cart && !cart.hasInvoiceLine ? <p className='ms-cart-icon__subtotal-message'>{props.resources.subTotalMessage}</p> : null;
};

/**
 *
 * CartIconView component.
 * @extends {React.PureComponent<ICartIconViewProps>}
 */
export class CartIconView extends React.PureComponent<
    ICartViewProps & ICartIconViewProps & ICartIconExtensionProps<{}>,
    ICartIconViewState
> {
    private isAutoDisplayTriggered: boolean;

    private readonly cartIconRef: React.RefObject<HTMLButtonElement> = React.createRef();

    public constructor(props: ICartViewProps & ICartIconViewState & ICartIconViewProps & ICartIconExtensionProps<{}>) {
        super(props);
        this.isAutoDisplayTriggered = false;
        this.state = {
            isModalOpen: false
        };
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            window.addEventListener('keydown', this._escFunction, false);
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('keydown', this._escFunction, false);
        }
    }

    /**
     * Render Cart Item count.
     * @returns JSX Element.
     */
    public render(): JSX.Element | null {
        const cart = this.props.data.cart.result ?? undefined;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- check config.
        const shouldShowMiniCart =
            cart?.isProductAddedToCart && this.props.context.app.config.addToCartBehavior === AddToCartBehavior.showMiniCart;
        if (shouldShowMiniCart) {
            if (!this.isAutoDisplayTriggered) {
                // First time trigger auto mini cart.
                this.setState({ isModalOpen: true });
                this.isAutoDisplayTriggered = true;
            } else if (!this.state.isModalOpen) {
                // If modal is closed, reset the flag.
                this.isAutoDisplayTriggered = false;
            }
        }

        // @CUSTOM: Kit display - need to validate react element before using its props for CartIconComponent below
        const cartIconProps = React.isValidElement(this.props.cartIcon) && this.props.cartIcon.props;
        const electivesQty = this._getElectiveItemCount(this.props);

        return (
            <Node {...this.props.miniCartWrapper} {...this.props.renderModuleAttributes(this.props)}>
                <button onClick={this._openModal} className='ms-cart-icon-wrapper msc-btn' ref={this.cartIconRef}>
                    {/* @CUSTOM: Kit display - replaced default this.props.cartIcon below with component to pass through adjusted cart item count */}
                    <CartIconComponent {...cartIconProps} electiveCount={electivesQty} />
                </button>
                <Node {...this._modalContainer()}>
                    <Node className='ms-cart-icon__header msc-modal__header'>
                        {this.props.flyoutTitle}
                        {this._renderCartItemCount(this.props)}
                        {this._renderCartCloseIcon()}
                    </Node>
                    <Node className='ms-cart-icon__body msc-modal__body'>
                        {this.props.data.cart.result?.isEmpty ? this.props.slots.emptyCart : null}
                        {this.props.checkoutBlockedDueToUnavailableFunds}
                        <Node {...this.props.CartlinesWrapper}>{renderCartlines(this.props.cartlines, this.props)}</Node>
                    </Node>
                    <Node className='ms-cart-icon__footer msc-modal__footer'>
                        {this.props.slots.promoContentItem}
                        {renderTotalPrice(this.props)}
                        {renderSubtotalMessage(this.props)}
                        {/* @CUSTOM: Moved Checkout, Guest Checkout, Go to Cart buttons to btn-section div below */}
                        <Node className='ms-cart-icon__btn-section'>
                            {this.props.checkoutAsSignInUserButton}
                            {/* @CUSTOM Removed Guest Checkout {this.props.checkoutAsGuestButton} */}
                            {this.props.data.cart.result?.isEmpty ? null : this.props.goToCartButton}
                            {this.props.data.cart.result?.isEmpty ? (
                                <Button
                                    className='ms-cart-icon__btn-backtoshopping'
                                    href={getUrlSync('home', this.props.context.actionContext)}
                                >
                                    {this.props.resources.continueShoppingButtonTitle}
                                </Button>
                            ) : null}
                        </Node>
                    </Node>
                </Node>
            </Node>
        );
    }

    /**
     * Initiate modal container.
     * @returns Inode props.
     */
    private readonly _modalContainer = (): INodeProps => {
        return {
            tag: Modal,
            placement: 'bottom-end',
            hideArrow: true,
            className: classnames('ms-cart-icon__minicartmodal-container', this.props.config.className),    // @CUSTOM: Added config CSS class
            wrapClassName: 'ms-cart-icon__minicartmodal',
            isOpen: this.state.isModalOpen,
            toggle: this.closeModal
        };
    };

    /**
     * Initiate open modal.
     */
    private readonly _openModal = (): void => {
        const payLoad = getPayloadObject('click', this.props.telemetryContent!, 'cart-icon', '');
        onTelemetryClick(this.props.telemetryContent!, payLoad, 'cart-icon');
        this.setState({
            isModalOpen: true
        });
    };

    /**
     * Initiate close modal.
     */
    private readonly closeModal = (): void => {
        this.cartIconRef.current?.focus();
        this.setState({
            isModalOpen: false
        });
    };

    /**
     * Initiate cart close Icon.
     * @returns JSX Element.
     */
    private readonly _renderCartCloseIcon = (): JSX.Element | null => {
        return <Button className='ms-cart-icon__btn-close' aria-label='Close' onClick={this.closeModal} />;
    };

    //------------------------------------------------------
    // @CUSTOM: Kit display - obtain elective item count to
    // subtract from total cart item count
    //------------------------------------------------------
    private readonly _getElectiveItemCount = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): number => {
        const cartData = props.data.cart.result;
        const electivesCartLines = cartData?.cart.CartLines?.filter(cartLine => {
            const attributes = cartLine.AttributeValues;
            const parsedAttributes = attributes && convertCartAttributes(attributes);
            return parsedAttributes && parsedAttributes[attrNames.kitLineParentAssociation];
        });
        const electivesQty = electivesCartLines?.map(electiveCartLine => {
            return electiveCartLine.Quantity || 0;
        });
        return electivesQty?.reduce((accumulator, currentValue) => {
            return accumulator + currentValue
        }, 0) || 0;
    }
    //------------------------------------------------------

    /**
     * Render Cart Item count.
     * @param props - Cart icon view props.
     * @returns JSX Element.
     */
    private readonly _renderCartItemCount = (props: ICartIconViewProps & ICartIconExtensionProps<{}>): JSX.Element | null => {
        const defaultCartItemCount: number = 0;

        //--------------------------------------------------
        // @CUSTOM: Kit display - obtain elective item count
        // to subtract from total cart item count
        //--------------------------------------------------
        const cartData = props.data.cart.result;
        const cartTotalItems = cartData?.totalItemsInCart;
        const electivesQtySum = this._getElectiveItemCount(props);
        const adjustedItemCount = electivesQtySum && cartTotalItems && (cartTotalItems - electivesQtySum);
        const cartItemlength = adjustedItemCount || cartTotalItems || defaultCartItemCount;
        //--------------------------------------------------

        const cartItemCount = `${cartItemlength} ${props.resources.items}`;

        return <Node className='ms-cart-icon__count'>{cartItemCount}</Node>;
    };

    /**
     * Handle escape click to close modal.
     * @param event - On press of any key.
     */
    private readonly _escFunction = (event: KeyboardEvent) => {
        if (event.keyCode === KeyCodes.Escape && this.state.isModalOpen) {
            this.closeModal();
        }
    };
}

export default CartIconView;
